import React from 'react';
import PropTypes from 'prop-types';
import {Box, CircularProgress, Typography} from '@mui/material';
import SoftBox from '../SoftBox';
import SoftTypography from '../SoftTypography';
import colors from '../../assets/theme/base/colors';

/**
 * Wrapper component that provides loading states and no data indicators for tables
 */
function TableWithLoading({
                              children,
                              isLoading = false,
                              data = [],
                              loadingMessage = "Loading data...",
                              noDataMessage = "No data available",
                              minHeight = "200px"
                          }) {
    const {grey} = colors;

    // Show loading state
    if (isLoading) {
        return (
            <SoftBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={minHeight}
                p={3}
            >
                <CircularProgress
                    size={40}
                    thickness={4}
                    sx={{
                        color: (theme) => theme.palette.primary.main,
                        marginBottom: 2,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%': {opacity: 1},
                            '50%': {opacity: 0.7},
                            '100%': {opacity: 1},
                        },
                    }}
                />
                <SoftTypography
                    variant="body2"
                    color="text"
                    opacity={0.7}
                    textAlign="center"
                    fontWeight="medium"
                >
                    {loadingMessage}
                </SoftTypography>
            </SoftBox>
        );
    }

    // Show no data state
    if (!data || data.length === 0) {
        return (
            <SoftBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={minHeight}
                p={3}
                sx={{
                    backgroundColor: grey[50],
                    borderRadius: 1,
                    border: `1px solid ${grey[200]}`,
                }}
            >
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: grey[100],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '24px',
                            color: grey[400],
                        }}
                    >
                        📊
                    </Typography>
                </Box>
                <SoftTypography
                    variant="h6"
                    color="text"
                    textAlign="center"
                    fontWeight="medium"
                    mb={1}
                >
                    {noDataMessage}
                </SoftTypography>
                <SoftTypography
                    variant="body2"
                    color="text"
                    opacity={0.6}
                    textAlign="center"
                >
                    Try adjusting your filters or add some data to get started.
                </SoftTypography>
            </SoftBox>
        );
    }

    // Show table with data
    return children;
}

TableWithLoading.propTypes = {
    children: PropTypes.node.isRequired,
    isLoading: PropTypes.bool,
    data: PropTypes.array,
    loadingMessage: PropTypes.string,
    noDataMessage: PropTypes.string,
    minHeight: PropTypes.string,
};

export default TableWithLoading;
